import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context';
import styles from './RegisterPage.module.css';

// ---------------------------------------------------------------------------
// Eye icon SVGs — open and closed variants
// ---------------------------------------------------------------------------

function EyeOpenIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeClosedIcon(): JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// MangoGrid power-grid icon — hexagonal with internal lines (amber)
// ---------------------------------------------------------------------------

function MangoGridIcon({ size = 48 }: { size?: number }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Outer hexagon */}
      <polygon
        points="24,4 42,14 42,34 24,44 6,34 6,14"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      {/* Internal grid lines */}
      <line x1="24" y1="4" x2="24" y2="44" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="6" y1="14" x2="42" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      <line x1="42" y1="14" x2="6" y2="34" stroke="#f59e0b" strokeWidth="1.5" strokeOpacity="0.55" />
      {/* Power bolt */}
      <polygon
        points="26,16 20,26 24,26 22,34 30,22 26,22"
        fill="#f59e0b"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Left panel background SVG — abstract transmission network art
// ---------------------------------------------------------------------------

function GridArtSVG(): JSX.Element {
  return (
    <svg
      className={styles.leftPanelArt}
      viewBox="0 0 900 700"
      preserveAspectRatio="xMidYMid slice"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {/* Secondary fine network — faint slate lines */}
      <g stroke="rgba(148,163,184,0.08)" strokeWidth="1">
        <line x1="0" y1="100" x2="900" y2="100" />
        <line x1="0" y1="200" x2="900" y2="250" />
        <line x1="0" y1="350" x2="900" y2="400" />
        <line x1="0" y1="500" x2="900" y2="550" />
        <line x1="0" y1="620" x2="900" y2="620" />
        <line x1="150" y1="0" x2="150" y2="700" />
        <line x1="350" y1="0" x2="380" y2="700" />
        <line x1="550" y1="0" x2="520" y2="700" />
        <line x1="720" y1="0" x2="760" y2="700" />
        <line x1="900" y1="80" x2="700" y2="700" />
        <line x1="0" y1="180" x2="200" y2="700" />
        <line x1="100" y1="0" x2="800" y2="700" />
        <line x1="0" y1="300" x2="900" y2="450" />
      </g>

      {/* Primary transmission lines — electric blue; healthy grid, not warning state */}
      <g stroke="rgba(14,165,233,0.18)" strokeWidth="2" strokeLinecap="round">
        {/* Main diagonal spine */}
        <polyline points="30,680 180,420 400,280 620,180 870,60" />
        {/* Secondary trunk — bottom-left to top-right */}
        <polyline points="0,520 140,380 340,260 560,160 760,80 900,30" />
        {/* Lateral branch — left to right, middle */}
        <polyline points="0,340 200,310 420,340 600,300 800,260 900,240" />
        {/* Spur from main to south-east */}
        <polyline points="400,280 480,380 560,480 640,580 700,660" />
        {/* Spur from secondary trunk to south */}
        <polyline points="340,260 360,380 340,480 380,600" />
        {/* Northern horizontal line */}
        <polyline points="60,120 240,100 480,110 700,90 880,100" />
        {/* Western radial */}
        <polyline points="0,180 120,240 200,310" />
        {/* Eastern radial */}
        <polyline points="900,380 760,360 640,340 560,300" />
      </g>

      {/* Substation nodes — electric blue circles at junctions */}
      <g fill="rgba(14,165,233,0.35)" stroke="rgba(14,165,233,0.55)" strokeWidth="1">
        <circle cx="180" cy="420" r="5" />
        <circle cx="400" cy="280" r="6" />
        <circle cx="620" cy="180" r="5" />
        <circle cx="140" cy="380" r="4" />
        <circle cx="340" cy="260" r="5" />
        <circle cx="560" cy="160" r="4" />
        <circle cx="760" cy="80" r="4" />
        <circle cx="200" cy="310" r="5" />
        <circle cx="420" cy="340" r="4" />
        <circle cx="600" cy="300" r="5" />
        <circle cx="800" cy="260" r="4" />
        <circle cx="480" cy="380" r="4" />
        <circle cx="640" cy="580" r="5" />
        <circle cx="240" cy="100" r="4" />
        <circle cx="480" cy="110" r="5" />
        <circle cx="700" cy="90" r="4" />
      </g>

      {/* Bright accent nodes — key substations */}
      <g fill="rgba(14,165,233,0.60)" stroke="rgba(56,189,248,0.45)" strokeWidth="1.5">
        <circle cx="400" cy="280" r="8" />
        <circle cx="600" cy="300" r="7" />
        <circle cx="200" cy="310" r="7" />
        <circle cx="640" cy="580" r="6" />
      </g>

      {/* Subtle radial glow around primary hub */}
      <circle cx="400" cy="280" r="40" fill="rgba(14,165,233,0.03)" />
      <circle cx="600" cy="300" r="30" fill="rgba(14,165,233,0.03)" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Form state type
// ---------------------------------------------------------------------------

interface RegisterForm {
  name: string;
  email: string;
  username: string;
  password: string;
  confirm: string;
}

// ---------------------------------------------------------------------------
// RegisterPage
// ---------------------------------------------------------------------------

export default function RegisterPage(): JSX.Element {
  const [form, setForm] = useState<RegisterForm>({
    name: '',
    email: '',
    username: '',
    password: '',
    confirm: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterForm, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const navigate = useNavigate();
  const { login } = useAuth();

  function validate(): boolean {
    const e: Partial<Record<keyof RegisterForm, string>> = {};
    if (!form.name.trim()) {
      e.name = 'Full name is required.';
    }
    if (!form.email.includes('@')) {
      e.email = 'Enter a valid email address.';
    }
    if (!form.username.trim()) {
      e.username = 'Username is required.';
    }
    if (form.password.length < 8) {
      e.password = 'Password must be at least 8 characters.';
    } else if (/^[a-zA-Z]+$/.test(form.password) || /^\d+$/.test(form.password)) {
      e.password = 'Password must include both letters and numbers.';
    }
    if (form.password !== form.confirm) {
      e.confirm = 'Passwords do not match.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!validate()) return;

    setApiError(null);
    setLoading(true);

    try {
      // Step 1: Register the user in the database
      const regRes = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          password: form.password,
          email: form.email,
        }),
      });
      const regData: Record<string, unknown> = await regRes.json();
      if (!regRes.ok) {
        setApiError((regData.detail as string | undefined) ?? 'Registration failed.');
        return;
      }

      // Step 2: Log in immediately to get a real JWT
      const loginRes = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          password: form.password,
        }),
      });
      const loginData: Record<string, unknown> = await loginRes.json();
      if (!loginRes.ok) {
        setApiError((loginData.detail as string | undefined) ?? 'Account created but sign-in failed. Please sign in manually.');
        return;
      }

      login(loginData.access_token as string, {
        username: form.username,
        accountType: 'public',
        subscription: (loginData.subscription as string | undefined) ?? 'basic',
      });
      navigate('/dashboard', { replace: true });
    } catch {
      setApiError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleChange(field: keyof RegisterForm): (e: React.ChangeEvent<HTMLInputElement>) => void {
    return (e: React.ChangeEvent<HTMLInputElement>): void => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      if (apiError) setApiError(null);
      // Clear the per-field error as the user types
      if (errors[field]) {
        setErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };
  }

  return (
    <div className={styles.page}>
      {/* ================================================================
          Left panel — dark, geographic grid art
          ================================================================ */}
      <div className={styles.leftPanel} aria-hidden="true">
        <GridArtSVG />

        <div className={styles.leftPanelContent}>
          {/* Brand */}
          <div className={styles.brandRow}>
            <MangoGridIcon size={48} />
            <span className={styles.wordmark}>MangoGridStudio</span>
          </div>

          {/* Tagline */}
          <p className={styles.tagline}>
            Power Grid Optimization &amp; Analysis Platform
          </p>

          {/* Feature bullets */}
          <ul className={styles.featureList}>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              Real-time constraint mapping
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              Unit commitment optimization
            </li>
            <li className={styles.featureItem}>
              <span className={styles.featureDot} />
              ISO market intelligence
            </li>
          </ul>
        </div>
      </div>

      {/* ================================================================
          Right panel — white form panel
          ================================================================ */}
      <div className={styles.rightPanel}>
        {/* Mobile-only header (left panel is hidden on mobile) */}
        <div className={styles.mobileHeader}>
          <MangoGridIcon size={32} />
          <span
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            MangoGridStudio
          </span>
        </div>
        <p className={styles.mobileSubtext}>Network Operations Platform</p>

        {/* Form card */}
        <div className={styles.formCard}>
          {/* Form brand row */}
          <div className={styles.formBrandRow}>
            <MangoGridIcon size={32} />
            <span className={styles.formWordmark}>MangoGridStudio</span>
          </div>

          {/* Heading */}
          <h1 className={styles.formHeading}>Create your account</h1>
          <p className={styles.formSubheading}>
            Join the platform powering modern grid operations.
          </p>

          {/* Registration form */}
          <form
            data-testid="register-form"
            onSubmit={(ev) => void handleSubmit(ev)}
            aria-label="Create account form"
            noValidate
          >
            <div className={styles.fieldGroup}>
              {/* Full Name */}
              <div className={styles.field}>
                <label htmlFor="register-name" className={styles.label}>
                  Full Name
                </label>
                <input
                  id="register-name"
                  name="name"
                  type="text"
                  className={[styles.input, errors.name ? styles.inputError : ''].join(' ').trim()}
                  value={form.name}
                  onChange={handleChange('name')}
                  autoComplete="name"
                  autoFocus
                  placeholder="Jane Smith"
                />
                {errors.name !== undefined && (
                  <p className={styles.fieldError} role="alert">
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Email */}
              <div className={styles.field}>
                <label htmlFor="register-email" className={styles.label}>
                  Email
                </label>
                <input
                  id="register-email"
                  name="email"
                  type="email"
                  className={[styles.input, errors.email ? styles.inputError : ''].join(' ').trim()}
                  value={form.email}
                  onChange={handleChange('email')}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
                {errors.email !== undefined && (
                  <p className={styles.fieldError} role="alert">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Username */}
              <div className={styles.field}>
                <label htmlFor="register-username" className={styles.label}>
                  Username
                </label>
                <input
                  id="register-username"
                  name="username"
                  type="text"
                  className={[styles.input, errors.username ? styles.inputError : ''].join(' ').trim()}
                  value={form.username}
                  onChange={handleChange('username')}
                  autoComplete="username"
                  placeholder="janesmith"
                />
                {errors.username !== undefined && (
                  <p className={styles.fieldError} role="alert">
                    {errors.username}
                  </p>
                )}
              </div>

              {/* Password */}
              <div className={styles.field}>
                <label htmlFor="register-password" className={styles.label}>
                  Password
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="register-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    className={[styles.input, errors.password ? styles.inputError : ''].join(' ').trim()}
                    value={form.password}
                    onChange={handleChange('password')}
                    autoComplete="new-password"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={(): void => { setShowPassword((prev) => !prev); }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={0}
                  >
                    {showPassword ? <EyeClosedIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
                {errors.password !== undefined && (
                  <p className={styles.fieldError} role="alert">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className={styles.field}>
                <label htmlFor="register-confirm" className={styles.label}>
                  Confirm Password
                </label>
                <div className={styles.passwordWrapper}>
                  <input
                    id="register-confirm"
                    name="confirm"
                    type={showConfirm ? 'text' : 'password'}
                    className={[styles.input, errors.confirm ? styles.inputError : ''].join(' ').trim()}
                    value={form.confirm}
                    onChange={handleChange('confirm')}
                    autoComplete="new-password"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className={styles.eyeButton}
                    onClick={(): void => { setShowConfirm((prev) => !prev); }}
                    aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                    tabIndex={0}
                  >
                    {showConfirm ? <EyeClosedIcon /> : <EyeOpenIcon />}
                  </button>
                </div>
                {errors.confirm !== undefined && (
                  <p className={styles.fieldError} role="alert">
                    {errors.confirm}
                  </p>
                )}
              </div>
            </div>

            {/* API error */}
            {apiError !== null && (
              <p className={styles.fieldError} role="alert" style={{ marginBottom: 8 }}>
                {apiError}
              </p>
            )}

            {/* Create Account button */}
            <button type="submit" className={styles.btnPrimary} disabled={loading}>
              {loading ? 'Creating account…' : 'Create Account'}
            </button>

            {/* Sign-in link */}
            <div className={styles.signinRow}>
              <span>Already have an account?</span>
              <button
                type="button"
                className={styles.signinLink}
                onClick={(): void => { navigate('/login'); }}
              >
                Sign in
              </button>
            </div>
          </form>

          {/* Footer */}
          <footer className={styles.footer}>
            <span>© 2026 MangoGridStudio</span>
            <span className={styles.footerSep}>·</span>
            <a href="#privacy" className={styles.footerLink}>
              Privacy
            </a>
            <span className={styles.footerSep}>·</span>
            <a href="#terms" className={styles.footerLink}>
              Terms
            </a>
          </footer>
        </div>
      </div>
    </div>
  );
}
